import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// Separate client for storage uploads without keepalive (which has size limits)
const getStorageClient = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      headers: session?.access_token ? {
        Authorization: `Bearer ${session.access_token}`
      } : {}
    }
  });
};

interface DocumentPdf {
  messageId: string;
  pdfUrl: string;
  title: string | null;
  fileSize: number | null;
}

interface UseDocumentPdfResult {
  documentPdf: DocumentPdf | null;
  loading: boolean;
  generating: boolean;
  waitingForDocument: boolean;
  error: string | null;
  refetch: () => Promise<boolean>;
  triggerGeneration: () => Promise<void>;
}

interface UseDocumentPdfOptions {
  autoGenerate?: boolean;
}

const PDF_POLL_INTERVAL_MS = 2_000;
const PDF_POLL_MAX_ATTEMPTS = 30;

interface GeneratePdfResult {
  success?: boolean;
  error?: string;
  message?: string;
}

const requestPdfGeneration = async (messageId: string, userId: string): Promise<GeneratePdfResult> => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw new Error(sessionError.message);
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_PUBLISHABLE_KEY,
  };

  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-pdf`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ messageId, userId }),
  });

  const result = await response.json().catch(() => null) as GeneratePdfResult | null;

  if (response.status === 402) {
    const reason = result?.error;
    if (reason === 'guest_ip_limit_reached' || reason === 'guest_document_limit') {
      throw new Error(result?.message || 'Guest preview limit reached. Please sign up to continue.');
    }
    throw new Error(result?.message || 'Insufficient credits to generate PDF.');
  }

  if (!response.ok) {
    throw new Error(result?.error || result?.message || `PDF generation failed with status ${response.status}`);
  }

  if (!result) {
    throw new Error('PDF generation failed');
  }

  return result;
};

/**
 * Hook to fetch and manage PDF documents for a message
 */
export const useDocumentPdf = (
  messageId: string | null,
  userId?: string | null,
  options: UseDocumentPdfOptions = {},
): UseDocumentPdfResult => {
  const autoGenerate = options.autoGenerate === true;
  const [documentPdf, setDocumentPdf] = useState<DocumentPdf | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [waitingForDocument, setWaitingForDocument] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef(0);
  // Use a ref to track if document was found (avoids stale closure issue in polling)
  const documentFoundRef = useRef(false);
  // Track if we've already triggered generation for this message
  const generationTriggeredRef = useRef<string | null>(null);
  // Track the current pdfUrl for cleanup
  const pdfUrlRef = useRef<string | null>(null);

  const clearPollingTimer = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearPollingTimer();
    setWaitingForDocument(false);
  }, [clearPollingTimer]);

  const fetchDocument = useCallback(async (skipLoading = false): Promise<boolean> => {
    if (!messageId) {
      setDocumentPdf(null);
      setWaitingForDocument(false);
      return false;
    }

    if (!skipLoading) {
      setLoading(true);
      setError(null);
    }

    try {
      // Fetch document metadata from documents_vr
      const { data, error: fetchError } = await supabase
        .from('documents_vr')
        .select('message_id, storage_path, title, file_size')
        .eq('message_id', messageId)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (data && data.storage_path) {
        // Download the PDF blob directly to avoid iframe cross-origin blocking
        const { data: blobData, error: downloadError } = await supabase.storage
          .from('documents')
          .download(data.storage_path);

        if (downloadError) {
          throw downloadError;
        }

        // Revoke old blob URL if exists
        if (pdfUrlRef.current) {
          URL.revokeObjectURL(pdfUrlRef.current);
        }

        // Create a local blob URL
        const pdfUrl = URL.createObjectURL(blobData);
        pdfUrlRef.current = pdfUrl;
        documentFoundRef.current = true;

        setDocumentPdf({
          messageId: data.message_id,
          pdfUrl,
          title: data.title,
          fileSize: data.file_size
        });
        stopPolling();
        return true;
      } else {
        setDocumentPdf(null);
        return false;
      }
    } catch (err) {
      console.error('Error fetching document PDF:', err);
      if (!skipLoading) {
        setError(err instanceof Error ? err.message : 'Failed to fetch document');
      }
      setDocumentPdf(null);
      return false;
    } finally {
      if (!skipLoading) setLoading(false);
    }
  }, [messageId, stopPolling]);

  const startPollingForDocument = useCallback(() => {
    if (!messageId || pollIntervalRef.current) return;

    pollAttemptsRef.current = 0;
    setWaitingForDocument(true);
    pollIntervalRef.current = setInterval(async () => {
      pollAttemptsRef.current += 1;
      const found = await fetchDocument(true);

      if (found || pollAttemptsRef.current >= PDF_POLL_MAX_ATTEMPTS) {
        stopPolling();
      }
    }, PDF_POLL_INTERVAL_MS);
  }, [fetchDocument, messageId, stopPolling]);

  // Trigger PDF generation via edge function
  const triggerGeneration = useCallback(async () => {
    if (!messageId || !userId) return;
    
    // Prevent duplicate generation triggers for the same message
    if (generationTriggeredRef.current === messageId) {
      console.log('[useDocumentPdf] Generation already triggered for this message');
      return;
    }
    
    generationTriggeredRef.current = messageId;
    stopPolling();
    setGenerating(true);
    setError(null);
    
    try {
      console.log(`[useDocumentPdf] Triggering PDF generation for message ${messageId}`);
      const result = await requestPdfGeneration(messageId, userId);
      
      if (result.success) {
        console.log('[useDocumentPdf] PDF generation succeeded, fetching document');
        // Reset poll attempts and start polling for the new PDF
        pollAttemptsRef.current = 0;
        documentFoundRef.current = false;
        const found = await fetchDocument();
        if (!found) {
          startPollingForDocument();
        }
      } else {
        throw new Error(result.error || 'PDF generation failed');
      }
    } catch (err) {
      console.error('[useDocumentPdf] PDF generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate PDF');
      generationTriggeredRef.current = null; // Allow retry
    } finally {
      setGenerating(false);
    }
  }, [messageId, userId, fetchDocument, startPollingForDocument, stopPolling]);

  useEffect(() => {
    stopPolling();
    setDocumentPdf(null);
    setError(null);
    setGenerating(false);
    setWaitingForDocument(false);
    pollAttemptsRef.current = 0;
    documentFoundRef.current = false;
    generationTriggeredRef.current = null;
    
    // Revoke old blob URL
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }

    if (!messageId) return;

    let cancelled = false;

    // Kick off an immediate fetch, then poll briefly in case generation already started elsewhere.
    const initializeDocument = async () => {
      const found = await fetchDocument();
      if (!cancelled && !found) {
        if (autoGenerate) {
          void triggerGeneration();
        } else {
          startPollingForDocument();
        }
      }
    };

    initializeDocument();
    
    // Cleanup on unmount or message change
    return () => {
      cancelled = true;
      clearPollingTimer();
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [autoGenerate, clearPollingTimer, fetchDocument, messageId, startPollingForDocument, stopPolling, triggerGeneration]);

  return { documentPdf, loading, generating, waitingForDocument, error, refetch: fetchDocument, triggerGeneration };
};

/**
 * Extract title from markdown content (first H1)
 */
const extractTitleFromMarkdown = (markdown: string): string => {
  const h1Match = markdown.match(/^#\s+(.+)$/m);
  return h1Match ? h1Match[1].trim() : 'Untitled Document';
};
/**
 * Check if a document PDF exists for a message
 */
export const checkDocumentExists = async (messageId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('documents_vr')
      .select('message_id')
      .eq('message_id', messageId)
      .maybeSingle();

    if (error) {
      console.error('Error checking document existence:', error);
      return false;
    }

    return !!data;
  } catch {
    return false;
  }
};
